/*
 *
 * Copyright (c) Vaisala Oyj. All rights reserved.
 *
 */

import * as JSURL from 'jsurl';
import * as React from 'react';

import type { NavigateOptions } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';

export function useQueryParam<T>(
  key: string
): [T | undefined, (newQuery: T, options?: NavigateOptions) => void, (options?: NavigateOptions) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramValue = searchParams.get(key);

  // JSURL is not necessary if T is always a string
  // allows use of more complex params
  const value = React.useMemo(() => JSURL.parse(paramValue), [paramValue]);

  const setValue = React.useCallback(
    (newValue: T, options?: NavigateOptions) => {
      let newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set(key, JSURL.stringify(newValue));
      setSearchParams(newSearchParams, options);
    },
    [key, searchParams, setSearchParams]
  );

  const removeValue = React.useCallback(
    (options?: NavigateOptions) => {
      let newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete(key);
      setSearchParams(newSearchParams, options);
    },
    [key, searchParams, setSearchParams]
  );

  return [value, setValue, removeValue];
}
